import { useEffect, useRef, useState } from "react";
import "./Home.css";
import {
  motion,
  useInView,
  useAnimation,
  useScroll,
  useTransform,
} from "framer-motion";
import RoomCard from "../../components/RoomCard/RoomCard";
import AnimatedText from "../../components/AnimateText/AnimateText";
import ScrollAnimatedSection from "../../components/AnimateSection/AnimateSection";
import Testimonials from "../../components/Testimonials/Testimonials";
import { HeaderBackground } from "../../components/HeaderBackground/HeaderBackground";
import ContactForm from "../../components/ContactForm/ContactForm";
import { Trans, useTranslation } from "react-i18next";

function Home() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-500px" });
  const mainControls = useAnimation();
  const { t } = useTranslation();

  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY < 50); // Hide when scrolled down, show at top
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["-50%", "70%"]);

  useEffect(() => {
    if (isInView) {
      mainControls.start("visible");
    }
  }, [isInView, mainControls]);

  return (
    <div className="Home" ref={ref}>
      <header className="Home-header">
        <HeaderBackground></HeaderBackground>

        <div className="landingPageStuff">
          <div className="overlayText">
            <AnimatedText
              text={t("home.welcomeTo")}
              className="HomeTitle"
              delay={0}
              style={{
                fontSize: 50,
                position: "relative",
                bottom: "50px",
                fontFamily: "volkorn",
              }}
            />
            <div
              className="title"
              style={{ position: "relative", bottom: "50px" }}
            >
              <AnimatedText text="SEVASTI" className="HomeTitle" delay={0.5} />
              <AnimatedText text="COUNTRY" className="HomeTitle2" delay={1} />
              <AnimatedText text="HOUSE" className="HomeTitle3" delay={1.5} />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 2, ease: "easeInOut" }}
            className="scrollIndicator"
            aria-label="Scroll down"
          >
            <span className="scrollIndicatorText">{t("home.scrollDown")}</span>
            <div className="scrollMouse">
              <div className="scrollMouseWheel" />
            </div>
            <div className="scrollChevrons">
              <span />
              <span />
            </div>
          </motion.div>
        </div>
      </header>

      <section className="aboutSection" ref={ref}>
        <div className="aboutOverlay">
          <img
            src={process.env.PUBLIC_URL + "logositeurl.png"}
            alt="Logo"
            className="aboutLogo"
          />
          <h2 className="aboutTitle">
            <Trans>about.title</Trans>
          </h2>
          <p className="aboutDescription">
              <Trans>about.descHome</Trans>
          </p>
        </div>
      </section>
      <motion.section
        className="firstImage"
        style={{ position: "relative", zIndex: 0 }}
      >
        <motion.div className="parallaxImage" style={{ y }} />
      </motion.section>
      <section className="Rooms-section">
        <div className="roomsTitle">
          <h1>
            <ScrollAnimatedSection
              text="ourooms"
              className="ContentHighlight"
              style={{
                fontSize: "clamp(2rem, 5vw, 3rem)",
                padding: "10px",
              }}
            />
          </h1>
        </div>
        <div className="rooms">
          <div className="room1">
            <motion.div
              className="room1"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <RoomCard
                name="apartment1_name"
                text="apartment1_desc"
                path="Sevasti-Rooms/room1/016.JPG"
                href="/rooms#room-1"
              ></RoomCard>
            </motion.div>
          </div>
          <div className="room2">
            <motion.div
              className="room1"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <RoomCard
                name="apartment2_name"
                text="apartment2_desc"
                path="Sevasti-Rooms/room2/030.JPG"
                href="/rooms#room-2"
              ></RoomCard>
            </motion.div>
          </div>
          <div className="room3">
            <motion.div
              className="room1"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <RoomCard
                name="apartment3_name"
                text="apartment3_desc"
                path="Sevasti-Rooms/room3/room3-1.jpg"
                ribbon={t("roomsPage.limitedAvailability")}
                href="/rooms#room-3"
              ></RoomCard>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="testimonialsSection"> 
        <Testimonials />
      </section>
      <section className="contactSection">
        <h2 className="contactTitle">{t("contactForm.heading")}</h2>
        <p className="contactDescription">
          {t("contactForm.subheading")}
        </p>
        <ContactForm />
      </section>
    </div>
  );
}

export default Home;
